/**
 * `useUploadLimits` — the platform's ingestion policy
 * (`document.upload_limits`), driving both the file picker's `accept` list
 * and the client-side pre-validation.
 *
 * Static platform policy, so it is fetched once and cached for the session
 * (`staleTime: Infinity`). It is read-only and scope-free — no case context
 * needed.
 *
 * Fail-soft ON PURPOSE: if the call fails, `limits` is `undefined`,
 * `preValidateUpload` allows the upload through, and the server does the
 * enforcing it was always going to do. Blocking a lawyer's upload because a
 * read-only policy fetch failed would be a worse failure than the round-trip
 * rejection it was meant to save.
 */
import { useQuery } from "@tanstack/react-query";

import { useGatewayStore } from "@/stores/useGatewayStore";
import type { DocumentUploadLimits } from "@/types/gateway";

export function useUploadLimits(): {
  limits: DocumentUploadLimits | undefined;
  isLoading: boolean;
} {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const ready = Boolean(rpc) && connectionState === "connected";

  const { data, isLoading } = useQuery<DocumentUploadLimits, Error>({
    queryKey: ["document-upload-limits"],
    queryFn: async () => {
      const client = useGatewayStore.getState().rpc;
      if (!client) throw new Error("gateway not connected");
      return client.documentUploadLimits();
    },
    enabled: ready,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  return { limits: data, isLoading };
}
